import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Kbd,
  Tooltip,
} from '@portal/ui';
import {
  apiFetch,
  meQueryKey,
  signOut,
  useLocalize,
  useMe,
  useResetSession,
} from '@portal/web-sdk';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import {
  ChevronRight,
  Languages,
  LogOut,
  Menu,
  Monitor,
  Moon,
  Search,
  Star,
  Sun,
  UserRound,
} from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { NotificationBell } from '@portal/module-core-notification/web';
import { useTheme, type ThemePreference } from '../theme';
import { findTrail, useFavorites, useNavigation } from './navigation';

export function Topbar({
  onOpenCommand,
  onOpenMobileNav,
}: {
  onOpenCommand: () => void;
  onOpenMobileNav: () => void;
}) {
  const { t } = useTranslation();
  const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);

  return (
    <header className="sticky top-0 z-30 flex h-12 shadow-[inset_0_2px_0_var(--brand)] shrink-0 items-center gap-2 border-b bg-background/85 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/70 md:px-4">
      <Button
        variant="ghost"
        size="icon-sm"
        className="md:hidden"
        onClick={onOpenMobileNav}
        aria-label={t('shell.openMenu')}
      >
        <Menu />
      </Button>
      <Breadcrumb />
      <FavoriteToggle />
      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          onClick={onOpenCommand}
          className="hidden h-8 w-64 items-center gap-2 rounded-md border bg-surface px-2.5 text-sm text-muted-foreground transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:outline-none sm:flex"
        >
          <Search className="size-3.5" />
          <span className="flex-1 text-left">{t('shell.searchShort')}…</span>
          <Kbd>{isMac ? '⌘' : 'Ctrl'}</Kbd>
          <Kbd>K</Kbd>
        </button>
        <Button
          variant="ghost"
          size="icon-sm"
          className="sm:hidden"
          onClick={onOpenCommand}
          aria-label={t('shell.searchShort')}
        >
          <Search />
        </Button>
        <NotificationBell />
        <UserMenu />
      </div>
    </header>
  );
}

/** Star next to the breadcrumb: pins the current page to the sidebar's Favorites. */
function FavoriteToggle() {
  const { t } = useTranslation();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigation();
  const favorites = useFavorites();
  const node = findTrail(nav.data?.items ?? [], pathname).at(-1);
  if (!node?.path) return null;
  const pinned = favorites.isFavorite(node.id);
  const label = pinned ? t('shell.removeFavorite') : t('shell.addFavorite');
  return (
    <Tooltip content={label}>
      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={label}
        aria-pressed={pinned}
        onClick={() => favorites.toggle(node.id)}
      >
        <Star className={pinned ? 'fill-warning text-warning' : 'text-muted-foreground'} />
      </Button>
    </Tooltip>
  );
}

function Breadcrumb() {
  const { t } = useTranslation();
  const localize = useLocalize();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const nav = useNavigation();
  const trail = findTrail(nav.data?.items ?? [], pathname);

  return (
    <nav aria-label="Breadcrumb" className="min-w-0">
      <ol className="flex min-w-0 items-center gap-1 text-sm">
        {trail.length === 0 ? <li className="font-medium">{t('shell.home')}</li> : null}
        {trail.map((node, index) => {
          const last = index === trail.length - 1;
          return (
            <Fragment key={node.id}>
              {/* On phones only the current page is shown, so separators are hidden too. */}
              {index > 0 ? (
                <ChevronRight
                  className="hidden size-3.5 shrink-0 text-muted-foreground sm:block"
                  aria-hidden
                />
              ) : null}
              <li
                className={
                  last ? 'truncate font-medium' : 'hidden truncate text-muted-foreground sm:block'
                }
              >
                {node.path && !last ? (
                  <Link to={node.path} className="hover:text-foreground">
                    {localize(node.label)}
                  </Link>
                ) : (
                  <span aria-current={last ? 'page' : undefined}>{localize(node.label)}</span>
                )}
              </li>
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  return (
    ((parts.at(-1)?.[0] ?? '') + (parts.length > 1 ? (parts[0]?.[0] ?? '') : '')).toUpperCase() ||
    '?'
  );
}

function UserMenu() {
  const { t, i18n } = useTranslation();
  const { preference, setPreference } = useTheme();
  const { data: me } = useMe();
  const navigate = useNavigate();
  const resetSession = useResetSession();
  const queryClient = useQueryClient();

  const changeLanguage = (language: string) => {
    void i18n.changeLanguage(language);
    // Remember it on the account too, so it follows the user to other devices.
    void apiFetch('me', { method: 'PATCH', body: JSON.stringify({ locale: language }) })
      .then(() => queryClient.invalidateQueries({ queryKey: meQueryKey }))
      .catch(() => undefined);
  };

  const logOut = async () => {
    await signOut();
    await resetSession();
    await navigate({ to: '/login' });
  };

  const name = me?.user.name ?? '';
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ml-1 rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring/60"
          aria-label={name || t('user.account')}
        >
          <Avatar>
            {me?.user.image ? (
              <AvatarImage src={me.user.image} alt="" referrerPolicy="no-referrer" />
            ) : null}
            <AvatarFallback>{initials(name)}</AvatarFallback>
          </Avatar>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-foreground">
          <div className="truncate text-sm font-medium">{name}</div>
          <div className="mt-0.5 truncate text-xs font-normal text-muted-foreground">
            {me?.user.email}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void navigate({ to: '/account' })}>
          <UserRound />
          {t('user.account')}
        </DropdownMenuItem>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            {preference === 'dark' ? <Moon /> : preference === 'light' ? <Sun /> : <Monitor />}
            {t('user.theme')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={preference}
              onValueChange={(v) => setPreference(v as ThemePreference)}
            >
              <DropdownMenuRadioItem value="light">{t('user.themeLight')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="dark">{t('user.themeDark')}</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="system">{t('user.themeSystem')}</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSub>
          <DropdownMenuSubTrigger>
            <Languages />
            {t('user.language')}
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent>
            <DropdownMenuRadioGroup
              value={i18n.resolvedLanguage ?? 'vi'}
              onValueChange={changeLanguage}
            >
              <DropdownMenuRadioItem value="vi">Tiếng Việt</DropdownMenuRadioItem>
              <DropdownMenuRadioItem value="en">English</DropdownMenuRadioItem>
            </DropdownMenuRadioGroup>
          </DropdownMenuSubContent>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logOut()}>
          <LogOut />
          {t('user.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
